/**
 * VYRA Main JavaScript
 * Premium Wellness Theme
 *
 * Handles:
 *  - Scroll-triggered animations (Intersection Observer)
 *  - Smooth scrolling for anchor links
 *  - Cart count updater (vyra:cart:updated events)
 *  - Announcement bar close handler
 *  - Lazy loading for images
 */

(function () {
  'use strict';

  /* ==========================================================================
     1. INTERSECTION OBSERVER - Scroll Animations
     ========================================================================== */

  /**
   * Adds the .is-visible class to .animate-on-scroll and .stagger-children
   * elements when they enter the viewport at 15% visibility.
   */
  function initScrollAnimations() {
    var targets = document.querySelectorAll(
      '.animate-on-scroll, .animate-on-scroll--scale, .stagger-children'
    );

    if (!targets.length) return;

    /* Respect user preference for reduced motion */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ==========================================================================
     2. SMOOTH SCROLL - Anchor Links
     ========================================================================== */

  /**
   * Intercepts clicks on anchor links (href="#...") and scrolls smoothly
   * to the target element. Accounts for the sticky header height.
   */
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href^="#"]');
      if (!link) return;

      var targetId = link.getAttribute('href');
      if (!targetId || targetId === '#') return;

      var targetEl;
      try {
        targetEl = document.querySelector(targetId);
      } catch (err) {
        /* Invalid selector - bail out */
        return;
      }

      if (!targetEl) return;

      e.preventDefault();

      var header = document.querySelector('.header');
      var headerOffset = header ? header.offsetHeight : 0;
      var elementPosition = targetEl.getBoundingClientRect().top + window.pageYOffset;
      var offsetPosition = elementPosition - headerOffset - 16;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      /* Update URL hash without jumping */
      if (history.pushState) {
        history.pushState(null, null, targetId);
      }
    });
  }

  /* ==========================================================================
     3. CART COUNT UPDATER
     ========================================================================== */

  /**
   * Listens for the custom vyra:cart:updated event (dispatched by add-to-cart
   * handlers throughout the theme) and fetches the latest cart state from
   * Shopify's /cart.js endpoint. Updates all elements with the .cart-count
   * class to reflect the new item count.
   */
  function initCartCountUpdater() {
    document.addEventListener('vyra:cart:updated', function () {
      fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart fetch failed');
          return response.json();
        })
        .then(function (cart) {
          var countElements = document.querySelectorAll('.cart-count');
          countElements.forEach(function (el) {
            el.textContent = cart.item_count;

            /* Toggle visibility if count is 0 */
            if (cart.item_count > 0) {
              el.style.display = '';
              el.removeAttribute('hidden');
            } else {
              el.setAttribute('hidden', '');
            }
          });
        })
        .catch(function (err) {
          /* Silently fail - cart count simply won't update */
          if (console && console.warn) {
            console.warn('[VYRA] Cart count update failed:', err);
          }
        });
    });
  }

  /* ==========================================================================
     4. ANNOUNCEMENT BAR CLOSE
     ========================================================================== */

  /**
   * Handles the close/dismiss button on the announcement bar.
   * Slides the bar up and stores the dismissal in sessionStorage
   * so it stays hidden for the current browsing session.
   */
  function initAnnouncementBarClose() {
    var STORAGE_KEY = 'vyra_announcement_dismissed';

    var bar = document.querySelector('.announcement-bar');
    if (!bar) return;

    /* Check if already dismissed this session */
    try {
      if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
        bar.style.display = 'none';
        return;
      }
    } catch (e) {
      /* sessionStorage not available - continue normally */
    }

    var closeBtn = bar.querySelector(
      '.announcement-bar__close, [data-announcement-close]'
    );
    if (!closeBtn) return;

    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();

      bar.style.transition = 'transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease';
      bar.style.transform = 'translateY(-100%)';
      bar.style.opacity = '0';

      setTimeout(function () {
        bar.style.display = 'none';
      }, 400);

      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch (e) {
        /* sessionStorage not available - dismissal won't persist */
      }
    });
  }

  /* ==========================================================================
     5. LAZY LOADING - Images
     ========================================================================== */

  /**
   * Sets up lazy loading for images with data-src and data-srcset attributes.
   * Uses native loading="lazy" as the primary mechanism and falls back to
   * Intersection Observer for images that need data-attribute swapping.
   */
  function initLazyLoading() {
    var lazyImages = document.querySelectorAll('img[data-src], img[data-srcset]');
    if (!lazyImages.length) return;

    /**
     * Load a single lazy image by swapping data attributes to real ones.
     */
    function loadImage(img) {
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      if (img.dataset.sizes) {
        img.sizes = img.dataset.sizes;
        img.removeAttribute('data-sizes');
      }
      img.classList.add('is-loaded');
    }

    /* Use Intersection Observer for performant lazy loading */
    if ('IntersectionObserver' in window) {
      var lazyObserver = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              loadImage(entry.target);
              obs.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: '200px 0px',
          threshold: 0
        }
      );

      lazyImages.forEach(function (img) {
        lazyObserver.observe(img);
      });
    } else {
      /* Fallback: load all images immediately */
      lazyImages.forEach(function (img) {
        loadImage(img);
      });
    }
  }

  /* ==========================================================================
     6. INIT
     ========================================================================== */

  /**
   * Boot everything once the DOM is ready.
   */
  function init() {
    initScrollAnimations();
    initSmoothScroll();
    initCartCountUpdater();
    initAnnouncementBarClose();
    initLazyLoading();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
